import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { toast } from "../components/ui/sonner";
import { FileDown } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const PRIORITE_COLOR = { FAIBLE: "bg-slate-100 text-slate-700", NORMALE: "bg-sky-100 text-sky-700", HAUTE: "bg-amber-100 text-amber-800", CRITIQUE: "bg-rose-100 text-rose-800" };
const STATUTS = ["A_FAIRE", "EN_COURS", "REALISEE", "VERIFIEE_CLOTUREE", "ANNULEE"];

export default function Actions() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [actions, setActions] = useState([]);
  const [sites, setSites] = useState([]);
  const [priorite, setPriorite] = useState("");
  const [statut, setStatut] = useState("");
  const [siteId, setSiteId] = useState("");
  const [selected, setSelected] = useState(null);
  const [comment, setComment] = useState("");

  const load = () => {
    const params = {};
    if (priorite) params.priorite = priorite;
    if (statut) params.statut = statut;
    if (siteId) params.site_id = siteId;
    api.get("/actions", { params }).then((res) => setActions(res.data));
  };

  useEffect(() => {
    api.get("/sites").then((res) => setSites(res.data));
  }, []);

  useEffect(load, [priorite, statut, siteId]);

  const canVerify = ["ADMIN", "DIRECTION_SECURITE", "RESPONSABLE_SITE"].includes(user?.role);

  const updateStatus = async (action, nextStatut) => {
    try {
      await api.patch(`/actions/${action.action_id}`, { statut: nextStatut, commentaire: comment });
      toast.success("Statut mis à jour");
      setSelected(null);
      setComment("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action non autorisée");
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-4" data-testid="actions-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-3xl font-black uppercase tracking-tight">Plan d'Actions</h1>
        <Button variant="outline" size="sm" className="gap-1.5" data-testid="actions-goto-rapports-button" onClick={() => navigate("/rapports")}>
          <FileDown className="h-4 w-4" /> Télécharger les rapports
        </Button>
      </div>
      <div className="flex gap-3 flex-wrap">
        <Select value={siteId || "ALL"} onValueChange={(v) => setSiteId(v === "ALL" ? "" : v)}>
          <SelectTrigger className="w-48" data-testid="action-filter-site"><SelectValue placeholder="Site" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Tous les sites</SelectItem>
            {sites.map((s) => <SelectItem key={s.site_id} value={s.site_id}>{s.nom_site}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={priorite || "ALL"} onValueChange={(v) => setPriorite(v === "ALL" ? "" : v)}>
          <SelectTrigger className="w-44" data-testid="action-filter-priority"><SelectValue placeholder="Priorité" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Toutes priorités</SelectItem>
            {["FAIBLE", "NORMALE", "HAUTE", "CRITIQUE"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statut || "ALL"} onValueChange={(v) => setStatut(v === "ALL" ? "" : v)}>
          <SelectTrigger className="w-52" data-testid="action-filter-statut"><SelectValue placeholder="Statut" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Tous statuts</SelectItem>
            {STATUTS.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        {actions.map((a) => (
          <Card key={a.action_id} data-testid="action-item-card">
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="font-mono text-xs text-muted-foreground">{a.code_lisible}</span>
                <Badge className={PRIORITE_COLOR[a.priorite] || ""}>{a.priorite}</Badge>
              </div>
              <p className="text-sm font-medium">{a.description_action}</p>
              <div className="text-xs font-semibold text-slate-600 dark:text-slate-300">{a.site_nom || a.site_id}</div>
              <div className="flex items-center justify-between text-xs text-muted-foreground flex-wrap gap-2">
                <span>Responsable: {a.responsable || "-"}</span>
                <span>Échéance: {a.echeance || "-"}</span>
              </div>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <Badge variant="outline">{(a.statut || "").replace(/_/g, " ")}</Badge>
                <div className="flex gap-2">
                  {a.statut === "A_FAIRE" && <Button size="sm" variant="outline" onClick={() => updateStatus(a, "EN_COURS")}>Démarrer</Button>}
                  {a.statut === "EN_COURS" && <Button size="sm" variant="outline" onClick={() => updateStatus(a, "REALISEE")}>Marquer réalisée</Button>}
                  {a.statut === "REALISEE" && canVerify && (
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setSelected(a)}>Vérifier / Clôturer</Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {actions.length === 0 && <p className="text-sm text-muted-foreground">Aucune action pour ces filtres.</p>}
      </div>

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent data-testid="action-verify-modal">
          <DialogHeader><DialogTitle>Vérification / Clôture de l'action</DialogTitle></DialogHeader>
          <Textarea placeholder="Commentaire de vérification" value={comment} onChange={(e) => setComment(e.target.value)} data-testid="action-verify-comment" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelected(null)}>Annuler</Button>
            <Button data-testid="action-verify-confirm-button" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => updateStatus(selected, "VERIFIEE_CLOTUREE")}>Confirmer la clôture</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
